/**
 * A PRE-PURCHASE INSPECTION IS PAID BEFORE IT IS DONE.
 *
 * Anthony, 2026-10-07. A PPI is a flat $225, labor only, and the shop
 * takes the money first, mostly by Zelle. The desk was built for the other
 * order: every bill waits for Done, the deposit card only shows when the
 * parts come to more than $100, and a payment recorded on a job with no
 * bill raises the FINAL bill, which closes the job. So staff marked
 * inspections Done days before anybody looked at the car (2163 was
 * completed 10-07 for a 10-09 inspection; 2120, 2128 and 1842 the same)
 * just to get a bill to put the Zelle against.
 *
 * The fix is to treat the money as what it is: a PREPAYMENT. It rides on
 * the deposit invoice (kind = 'deposit', migration 0075), which settles
 * itself and never touches the job (0101). The job stays open, gets booked
 * and inspected, and the final bill at the end nets the prepayment off --
 * a fully prepaid inspection closes at $0 due.
 *
 * Pure, no imports beyond the keyword rule, so the browser, the server and
 * the tests all say the same thing.
 */

import { isPrePurchase } from "./prepurchase.ts";

/** Supercanic. The only account the PPI Auto storefront applies to. */
export const PPI_ACCOUNT = "7097c673-aa9f-4f0e-ba24-75050a5a29ae";

/** The name customers see on a PPI from the national campaign. */
export const PPI_BRAND = "PPI Auto";
export const PPI_SITE = "https://prepurchaseauto.com";

type JobWords = {
  title?: string | null;
  complaint?: string | null;
  lines?: Array<{ description?: string | null }> | null;
};

/** Is this job a pre-purchase inspection, from anything written on it. */
export function isPpiJob(j: JobWords | null | undefined): boolean {
  if (!j) return false;
  return isPrePurchase(j.title, j.complaint, ...(j.lines ?? []).map((l) => l.description));
}

export type Storefront = { name: string; site: string; phone: string | null };

/**
 * WHO A PPI CUSTOMER IS TALKING TO.
 *
 * Anthony: the PPI campaign should stop showing "Supercanic" to customers.
 * The inspection is sold nationally as PPI Auto (prepurchaseauto.com), so a
 * buyer who booked there and then got a page, a text and an invoice signed
 * by a mobile mechanic in San Jacinto reasonably wonders who they paid.
 *
 * Only on Supercanic's own account, and only on a PPI. Every other shop and
 * every other Supercanic job keeps its own name; null means "change
 * nothing". The phone is the account's own unless PPI_PHONE is set, so the
 * call button still rings somebody.
 */
export function ppiStorefront(
  job: JobWords & { account_id?: string | null } | null | undefined,
  o: { accountId?: string | null; accountPhone?: string | null; ppiPhone?: string | null } = {},
): Storefront | null {
  const account = o.accountId ?? job?.account_id ?? null;
  if (account !== PPI_ACCOUNT) return null;
  if (!isPpiJob(job)) return null;
  const phone = String(o.ppiPhone ?? "").trim() || String(o.accountPhone ?? "").trim() || null;
  return { name: PPI_BRAND, site: PPI_SITE, phone };
}

/** What the deposit card and the customer's page call the money. */
export function prepayLabel(ppi: boolean, partsWord = "parts"): string {
  if (ppi) return "Prepayment";
  const p = String(partsWord || "parts");
  return `${p.charAt(0).toUpperCase()}${p.slice(1)} deposit`;
}

/**
 * THE TEXT THAT ASKS FOR IT, matching the page it links to.
 *
 * The quote used to say "book it here" and the page offered Approve and
 * nothing else, so the customer pressed a button that did not book
 * anything. Approving now raises the prepayment and the Pay button appears
 * right under it, so the sentence says exactly that.
 */
export function ppiPayLine(link: string): string {
  return `Approve and pay here to lock in your inspection: ${link}`;
}

/** The whole approve-and-pay text, for the bridge and the desk alike. */
export function ppiPayText(o: { first?: string | null; car?: string | null; amount: number; link: string }): string {
  const first = String(o.first ?? "").trim() || "there";
  const on = String(o.car ?? "").trim() ? ` on the ${String(o.car).trim()}` : "";
  return `Hi ${first}, your pre-purchase inspection${on} is ${usd(o.amount)}. ${ppiPayLine(o.link)}`;
}

const usd = (n: number) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Status words for a job that has not been inspected yet. */
const NOT_DONE = new Set(["estimate", "scheduled", "in_progress"]);

/**
 * WHERE A PAYMENT WITH NO INVOICE NAMED LANDS.
 *
 * The rule that made staff mark inspections Done: a payment that covers
 * the whole job on a job with no bill raised the FINAL bill, and a settled
 * final bill closes the job. On a PPI that has not been inspected yet the
 * money is a prepayment, however much of it there is, so it goes on the
 * deposit invoice and the job stays open.
 *
 * Everything else keeps the rule it had: short of the job total is a
 * deposit, the whole of it is the final bill.
 */
export function paymentLandsOn(o: {
  ppi: boolean; status: string; hasFinal: boolean; amount: number; jobTotal: number;
}): "existing" | "deposit" | "final" {
  if (o.hasFinal) return "existing";
  if (o.ppi && NOT_DONE.has(o.status)) return "deposit";
  if (o.jobTotal > 0 && o.amount < o.jobTotal - 0.005) return "deposit";
  return "final";
}

/**
 * THE FINAL BILL, LESS WHAT WAS ALREADY PAID AHEAD.
 *
 * `settled` is true when the prepayment covers the whole bill: the bill is
 * raised at $0 and closes the job on the spot. The trigger in 0024/0101
 * never sees it -- it fires on a PAYMENT, and a $0 bill gets none -- so
 * without this a prepaid inspection sat at "invoiced, $0 owing" for ever.
 */
export function finalNet(subtotal: number, depositPaid: number): {
  total: number; depositApplied: number; settled: boolean;
} {
  const depositApplied = Math.round(Math.max(0, depositPaid) * 100) / 100;
  const total = Math.max(0, Math.round((subtotal - depositApplied) * 100) / 100);
  return { total, depositApplied, settled: subtotal > 0 && depositApplied > 0.005 && total <= 0.005 };
}

/** Should the desk offer the prepayment card on this job? */
export function offersPrepay(o: { ppi: boolean; partsTotal: number; laborTotal: number; hasDeposit: boolean }): boolean {
  if (o.hasDeposit) return true;
  if (o.ppi) return true;
  if (o.partsTotal > 100) return true;
  // Labor only, priced: the same problem as a PPI with no keyword on it.
  return o.partsTotal <= 0.005 && o.laborTotal > 0.005;
}

/* ─── WHO IS WORKING IT ──────────────────────────────────────────────────
 *
 * Anthony, 2026-10-07: "if a shop desk member works on it, tag them as the
 * advisor, do the same inside mechanicrank where eber works so they don't
 * step on each others toes."
 *
 * A CLAIM. The first person to act on a PPI -- from the Shop Desk or from
 * the MechanicRank desk through the bridge -- becomes its advisor, and a
 * 'claimed' event says who and from where. After that the job is theirs:
 * the bridge refuses to reassign it (409) unless asked to take over, and
 * the desk warns, not blocks, somebody else acting on it.
 */

export type Via = "shop-desk" | "mechanicrank-desk";

export const VIA_LABEL: Record<string, string> = {
  "shop-desk": "Shop Desk",
  "mechanicrank-desk": "MechanicRank desk",
  "text-desk": "Text Desk",
  garage: "their own page",
};

export const viaLabel = (via: unknown): string =>
  VIA_LABEL[String(via ?? "")] ?? (String(via ?? "").trim() || "Shop Desk");

export type ClaimVerdict =
  | { act: "claim" }         // nobody has it: it is yours
  | { act: "same" }          // already yours
  | { act: "conflict" }      // somebody else has it, and you did not ask to take it
  | { act: "reassign" };     // somebody else has it, and you asked to take it over

export function claimVerdict(current: string | null | undefined, actor: string, takeover = false): ClaimVerdict {
  if (!current) return { act: "claim" };
  if (current === actor) return { act: "same" };
  return takeover ? { act: "reassign" } : { act: "conflict" };
}

/** "Advisor: Eber Curiel (via MechanicRank desk)" -- the plain line every screen shows. */
export function advisorLine(name: string | null | undefined, via?: string | null): string {
  const n = String(name ?? "").trim();
  if (!n) return "Advisor: nobody yet";
  return `Advisor: ${n} (via ${viaLabel(via ?? "shop-desk")})`;
}

/** Last ten digits, the shape every phone comparison here uses. */
export const phone10 = (v: unknown): string => String(v ?? "").replace(/\D/g, "").slice(-10);
